import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import { jobAPI } from '../../services/api';
import { getJobPosterLabel, getJobPosterUsername, shouldLinkJobPoster, getJobCompanyNameForMeta } from '../../utils/jobDisplay';
import { handleJobApply } from '../../utils/jobApply';
import Header from '../Header';
import Footer from '../Footer';
import BottomNavigationBar from '../BottomNavigationBar';
import JobApplicationForm from './JobApplicationForm';
import JobShareModal from './JobShareModal';
import ReportJobModal from './ReportJobModal';
import JobDescriptionContent from './JobDescriptionContent';
import './JobDetailPage.css';

const JobDetailPage = ({ 
  job: jobProp,
  onHomeClick, 
  onCategoriesClick, 
  onHowItWorksClick, 
  onProfileClick, 
  onAccountSettingsClick, 
  onForumClick 
}) => {
  const { id, token } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [job, setJob] = useState(jobProp || null);
  const [loading, setLoading] = useState(!jobProp);
  const [error, setError] = useState(null);
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [hasApplied, setHasApplied] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const [unhiding, setUnhiding] = useState(false);
  const [isReported, setIsReported] = useState(false);
  const [reportStatus, setReportStatus] = useState(null);

  // Get the job identifier (either id from /vacancies/:id or token from /job/:token)
  const jobIdentifier = token || id;

  // Update job state when prop changes
  useEffect(() => {
    if (jobProp) {
      setJob(jobProp);
      setIsHidden(jobProp.is_hidden || false);
      setIsReported(jobProp.is_reported || false);
      setReportStatus(jobProp.report_status || null);
      setLoading(false);
    }
  }, [jobProp]);

  useEffect(() => {
    // Only fetch if we have an identifier from URL and no job prop
    if (jobIdentifier && !jobProp) {
      fetchJobDetails();
    }
  }, [jobIdentifier, jobProp]);

  // Show login prompt for unauthenticated users viewing job details
  useEffect(() => {
    if (job && !isAuthenticated && !loading) {
      // Show login prompt after a short delay to let the page load
      const timer = setTimeout(() => {
        setShowLoginPrompt(true);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (isAuthenticated) {
      setShowLoginPrompt(false);
    }
  }, [job, isAuthenticated, loading]);

  const fetchJobDetails = async () => {
    if (!jobIdentifier) return;
    
    setLoading(true);
    setError(null);
    try {
      // The identifier can be either a job ID (numeric) or a share_token (for /job/{token} route)
      const response = await jobAPI.get(jobIdentifier);
      if (response.success) {
        setJob(response.data);
        setIsHidden(response.data.is_hidden || false);
        setIsReported(response.data.is_reported || false);
        setReportStatus(response.data.report_status || null);
      } else {
        setError(response.message || 'Failed to load job details');
      }
    } catch (err) {
      console.error('Error fetching job details:', err);
      setError(err.message || 'Failed to load job details');
    } finally {
      setLoading(false);
    }
  };


  const handleApplyClick = () => {
    handleJobApply(job, { isAuthenticated, navigate, setShowApplicationForm });
  };

  const handleApplicationSuccess = () => {
    setShowApplicationForm(false);
    setHasApplied(true);
    // Refresh job details to get updated application count
    fetchJobDetails();
  };

  const handleUnhideJob = async () => {
    if (!job || !isAuthenticated) return;
    
    setUnhiding(true);
    try {
      const response = await jobAPI.unhide(job.id);
      if (response.success) {
        setIsHidden(false);
        // Refresh job details to get updated state
        fetchJobDetails();
      } else {
        alert(response.message || 'Failed to unhide job');
      }
    } catch (err) {
      console.error('Error unhiding job:', err);
      alert('Failed to unhide job. Please try again.');
    } finally {
      setUnhiding(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatSalary = () => {
    if (!job || (!job.salary_min && !job.salary_max)) {
      return 'Not specified';
    }
    
    const currency = job.salary_currency || 'USD';
    const symbol = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : currency === 'GBP' ? '£' : currency;
    const period = job.salary_period === 'yearly' ? 'year' : 
                   job.salary_period === 'monthly' ? 'month' :
                   job.salary_period === 'hourly' ? 'hour' : job.salary_period;
    
    if (job.salary_min && job.salary_max) {
      return `${symbol}${job.salary_min.toLocaleString()} - ${symbol}${job.salary_max.toLocaleString()} per ${period}`;
    } else if (job.salary_min) {
      return `${symbol}${job.salary_min.toLocaleString()}+ per ${period}`;
    }
    return 'Not specified';
  };

  if (loading) {
    return (
      <div className="job-detail-page job-detail-with-bottom-nav">
        {/* Header - visible on desktop/tablet, hidden on mobile */}
        <div className="job-detail-header-nav">
          <Header 
            onLoginClick={() => navigate('/login')}
            onSignupClick={() => navigate('/signup')}
            isLoggedIn={isAuthenticated}
            user={user}
            onHomeClick={onHomeClick}
            onCategoriesClick={onCategoriesClick}
            onHowItWorksClick={onHowItWorksClick}
            onProfileClick={onProfileClick}
            onAccountSettingsClick={onAccountSettingsClick}
            onForumClick={onForumClick}
            onJobsClick={() => navigate('/vacancies')}
            currentPage="vacancies"
          />
        </div>
        <div className="job-detail-page-content">
          <div className="job-detail-loading">
            <div className="job-detail-spinner"></div>
            <span>Loading job details...</span>
          </div>
        </div>
        <Footer />
        <BottomNavigationBar 
          onHomeClick={onHomeClick}
          onCategoriesClick={onCategoriesClick}
          onHowItWorksClick={onHowItWorksClick}
          onProfileClick={onProfileClick}
          onAccountSettingsClick={onAccountSettingsClick}
          onForumClick={onForumClick}
          onJobsClick={() => navigate('/vacancies')}
          isLoggedIn={isAuthenticated}
          user={user}
        />
      </div>
    );
  }

  // Get base URL for OG tags
  const getBaseUrl = () => {
    if (typeof window !== 'undefined') {
      return window.location.origin;
    }
    return process.env.REACT_APP_API_URL || 'https://remotebirdy.com';
  };

  // Generate OG meta tags content
  const getOGContent = () => {
    const baseUrl = getBaseUrl();
    
    if (!job) {
      return {
        title: 'Remote Birdy - Find Your Dream Remote Job',
        description: 'Discover amazing remote job opportunities on Remote Birdy. Connect with top companies and find your perfect remote position.',
        image: `${baseUrl}/main-logo.png`,
        url: typeof window !== 'undefined' ? `${baseUrl}${window.location.pathname}` : baseUrl,
        type: 'website',
        siteName: 'Remote Birdy'
      };
    }

    const jobTitle = job.title || 'Job Opportunity';
    const companyName = getJobCompanyNameForMeta(job);
    const workType = job.work_type || '';
    const location = job.location || '';
    const salary = job.salary_min && job.salary_max 
      ? `$${job.salary_min.toLocaleString()} - $${job.salary_max.toLocaleString()}`
      : job.salary_min 
        ? `$${job.salary_min.toLocaleString()}+`
        : '';

    // Create description from job details
    let description = `${jobTitle} at ${companyName}`;
    if (workType) description += ` • ${workType}`;
    if (location) description += ` • ${location}`;
    if (salary) description += ` • ${salary}`;
    
    // Add job description excerpt if available
    if (job.description) {
      const cleanDescription = job.description.replace(/<[^>]*>/g, '').substring(0, 150);
      description += ` - ${cleanDescription}...`;
    } else {
      description += ' - Find your next remote opportunity on Remote Birdy';
    }

    // Use company logo if available, otherwise use Remote Birdy logo
    // Use PNG for better social media support (SVG might not work well on all platforms)
    let imageUrl = `${baseUrl}/main-logo.png`;
    if (job.company?.logo) {
      // If logo is a full URL, use it; otherwise make it absolute
      imageUrl = job.company.logo.startsWith('http') 
        ? job.company.logo 
        : `${baseUrl}${job.company.logo.startsWith('/') ? '' : '/'}${job.company.logo}`;
    }

    return {
      title: `${jobTitle} - Remote Birdy`,
      description: description.substring(0, 200), // Limit description length for OG tags
      image: imageUrl,
      url: `${baseUrl}/job/${jobIdentifier || id}`,
      type: 'article',
      siteName: 'Remote Birdy'
    };
  };

  const ogContent = getOGContent();
  
  // Ensure all ogContent values are strings (not undefined/null)
  const safeOGContent = {
    title: String(ogContent.title || 'Remote Birdy - Find Your Dream Remote Job'),
    description: String(ogContent.description || 'Discover amazing remote job opportunities on Remote Birdy.'),
    image: String(ogContent.image || ''),
    url: String(ogContent.url || ''),
    type: String(ogContent.type || 'website'),
    siteName: String(ogContent.siteName || 'Remote Birdy')
  };

  if (error || !job) {
    return (
      <>
        <Helmet>
          <title>{safeOGContent.title}</title>
          <meta name="description" content={safeOGContent.description} />
        </Helmet>
        <div className="job-detail-page job-detail-with-bottom-nav">
          {/* Header - visible on desktop/tablet, hidden on mobile */}
          <div className="job-detail-header-nav">
            <Header 
              onLoginClick={() => navigate('/login')}
              onSignupClick={() => navigate('/signup')}
              isLoggedIn={isAuthenticated}
              user={user}
              onHomeClick={onHomeClick}
              onCategoriesClick={onCategoriesClick}
              onHowItWorksClick={onHowItWorksClick}
              onProfileClick={onProfileClick}
              onAccountSettingsClick={onAccountSettingsClick}
              onForumClick={onForumClick}
              onJobsClick={() => navigate('/vacancies')}
              currentPage="vacancies"
            />
          </div>
          <div className="job-detail-page-content">
            <div className="job-detail-error">
              <h2>Job Not Found</h2>
              <p>{error || 'The job you are looking for does not exist or has been removed.'}</p>
              <button onClick={() => navigate('/vacancies')} className="back-to-jobs-btn">
                Back to Vacancies
              </button>
            </div>
          </div>
          <Footer />
          <BottomNavigationBar 
            onHomeClick={onHomeClick}
            onCategoriesClick={onCategoriesClick}
            onHowItWorksClick={onHowItWorksClick}
            onProfileClick={onProfileClick}
            onAccountSettingsClick={onAccountSettingsClick}
            onForumClick={onForumClick}
            onJobsClick={() => navigate('/vacancies')}
            isLoggedIn={isAuthenticated}
            user={user}
          />
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{safeOGContent.title}</title>
        <meta name="description" content={safeOGContent.description} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content={safeOGContent.type} />
        <meta property="og:url" content={safeOGContent.url} />
        <meta property="og:title" content={safeOGContent.title} />
        <meta property="og:description" content={safeOGContent.description} />
        <meta property="og:image" content={safeOGContent.image} />
        <meta property="og:site_name" content={safeOGContent.siteName} />
        
        {/* X (formerly Twitter) */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={safeOGContent.url} />
        <meta name="twitter:title" content={safeOGContent.title} />
        <meta name="twitter:description" content={safeOGContent.description} />
        <meta name="twitter:image" content={safeOGContent.image} />
        <meta name="twitter:site" content="@RemoteBirdy" />
        
        {/* Additional meta tags */}
        <meta name="robots" content="index, follow" />
        {job && job.created_at && (
          <meta property="article:published_time" content={String(job.created_at)} />
        )}
        {job && (job.company?.name || job.posted_by?.name || job.postedBy?.name) && (
          <meta property="article:author" content={String(job.company?.name || job.posted_by?.name || job.postedBy?.name)} />
        )}
      </Helmet>
      <div className="job-detail-page job-detail-with-bottom-nav">
      {/* Header - visible on desktop/tablet, hidden on mobile */}
      <div className="job-detail-header-nav">
        <Header 
          onLoginClick={() => navigate('/login')}
          onSignupClick={() => navigate('/signup')}
          isLoggedIn={isAuthenticated}
          user={user}
          onHomeClick={onHomeClick}
          onCategoriesClick={onCategoriesClick}
          onHowItWorksClick={onHowItWorksClick}
          onProfileClick={onProfileClick}
          onAccountSettingsClick={onAccountSettingsClick}
          onForumClick={onForumClick}
          onJobsClick={() => navigate('/vacancies')}
          currentPage="vacancies"
        />
      </div>
      
      <div className="job-detail-page-content">
        <div className="job-detail-container">
          <button 
            onClick={() => navigate('/vacancies')}
            className="back-button"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="19" y1="12" x2="5" y2="12"></line>
              <polyline points="12,19 5,12 12,5"></polyline>
            </svg>
            Back to Vacancies
          </button>

          {/* Login Prompt Banner for Unauthenticated Users */}
          {showLoginPrompt && !isAuthenticated && (
            <div className="login-prompt-banner">
              <div className="login-prompt-content">
                <div className="login-prompt-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path>
                    <polyline points="10 17 15 12 10 7"></polyline>
                    <line x1="15" y1="12" x2="3" y2="12"></line>
                  </svg>
                </div>
                <div className="login-prompt-text">
                  <h3>Login Required</h3>
                  <p>Please login to view full job details and apply for this position.</p>
                </div>
                <div className="login-prompt-actions">
                  <button 
                    onClick={() => {
                      setShowLoginPrompt(false);
                      navigate('/login', { state: { returnTo: window.location.pathname } });
                    }}
                    className="login-prompt-btn primary"
                  >
                    Login
                  </button>
                  <button 
                    onClick={() => {
                      setShowLoginPrompt(false);
                      navigate('/signup', { state: { returnTo: window.location.pathname } });
                    }}
                    className="login-prompt-btn secondary"
                  >
                    Sign Up
                  </button>
                  <button 
                    onClick={() => setShowLoginPrompt(false)}
                    className="login-prompt-close"
                    aria-label="Close"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Hidden Job Alert - Show to user who hid the job */}
          {isAuthenticated && isHidden && (
            <div className="job-hidden-alert">
              <div className="alert-content">
                <div className="alert-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                </div>
                <div className="alert-text">
                  <h4>This job is hidden for you</h4>
                  <p>You previously hid this job{isReported ? ' and reported it' : ''}. You can unhide it to see it in your job listings again.</p>
                </div>
                <button 
                  onClick={handleUnhideJob}
                  className="unhide-job-btn"
                  disabled={unhiding}
                >
                  {unhiding ? (
                    <>
                      <div className="spinner-small"></div>
                      Unhiding...
                    </>
                  ) : (
                    <>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                        <circle cx="12" cy="12" r="3"></circle>
                      </svg>
                      Unhide Job
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Non-Compliance Alert - Show to job owner */}
          {job.is_non_compliant && job.non_compliance_reason && (() => {
            // Check if current user is the job owner
            const actingUserId = user?.acting_user_id ?? user?.id;
            const isJobOwner = user && (
              job.posted_by_user_id === actingUserId ||
              job.posted_by?.id === actingUserId ||
              job.posted_by_user_id?.toString() === String(actingUserId)
            );

            // Only show to job owner
            if (isJobOwner) {
              return (
                <div className="job-non-compliance-alert">
                  <div className="alert-text">
                    <h4>Job Removed from Public Access</h4>
                    <p><strong>Reason:</strong> {job.non_compliance_reason}</p>
                    {job.removed_at && (
                      <p className="removed-date">Removed on: {new Date(job.removed_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                    )}
                  </div>
                </div>
              );
            }
            return null;
          })()}

          <div className="job-detail-card">
            <div className="job-detail-header">
              <div className="job-detail-title-section">
                <h1>{job.title}</h1>
                <div className="job-detail-meta">
                  {(() => {
                    const label = getJobPosterLabel(job);
                    const posterUsername = getJobPosterUsername(job);
                    const linkPoster = shouldLinkJobPoster(job);

                    if (linkPoster && posterUsername) {
                      return (
                        <a href={`/talent/${posterUsername}`} target="_blank" rel="noopener noreferrer" className="job-detail-company job-detail-company-link">{label}</a>
                      );
                    }

                    return (
                      <span className={`job-detail-company${job.is_managed_by_platform ? ' job-detail-platform-badge' : ''}`}>{label}</span>
                    );
                  })()}
                  {job.work_type && (
                    <>
                      <span className="job-detail-separator">•</span>
                      <span className="job-detail-type">{job.work_type}</span>
                    </>
                  )}
                  {job.job_type && (
                    <>
                      <span className="job-detail-separator">•</span>
                      <span className="job-detail-type">{job.job_type}</span>
                    </>
                  )}
                  {job.location && (
                    <>
                      <span className="job-detail-separator">•</span>
                      <span className="job-detail-location">{job.location}</span>
                    </>
                  )}
                </div>
              </div>
              {job.is_active && job.status === 'published' && (() => {
                const actingUserId = user?.acting_user_id ?? user?.id;
                const isJobOwner = user && (
                  job.posted_by_user_id === actingUserId ||
                  job.posted_by?.id === actingUserId ||
                  job.posted_by_user_id?.toString() === String(actingUserId)
                );
                
                return (
                  <div className="job-detail-actions">
                    <div className="job-actions-group">
                      {(() => {
                        if (isJobOwner) {
                          return (
                            <div className="job-owner-message">
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                <polyline points="22 4 12 14.01 9 11.01"></polyline>
                              </svg>
                              <span>Your Job Posting</span>
                            </div>
                          );
                        } else if (hasApplied) {
                          return (
                            <button className="applied-button" disabled>
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                <polyline points="22 4 12 14.01 9 11.01"></polyline>
                              </svg>
                              Applied
                            </button>
                          );
                        } else if (isAuthenticated) {
                          return (
                            <button 
                              onClick={handleApplyClick}
                              className="apply-button"
                            >
                              Apply Now
                            </button>
                          );
                        } else {
                          // Don't show Apply Now button for unauthenticated users
                          return null;
                        }
                      })()}
                      <button 
                        onClick={() => setShowShareModal(true)}
                        className="share-button"
                        title="Share this job"
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                        </svg>
                        Share
                      </button>
                      {!isJobOwner && (
                        <>
                          {isHidden ? (
                            // If job is hidden, show "Unhide Job" button instead of "Report" button
                            <button 
                              onClick={handleUnhideJob}
                              className="unhide-button-inline"
                              title="Unhide this job"
                              disabled={unhiding}
                            >
                              {unhiding ? (
                                <>
                                  <div className="spinner-small"></div>
                                  Unhiding...
                                </>
                              ) : (
                                <>
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                    <circle cx="12" cy="12" r="3"></circle>
                                  </svg>
                                  Unhide Job
                                </>
                              )}
                            </button>
                          ) : isReported ? (
                            // If job is reported but not hidden, show "Reported" button
                            <button 
                              className="report-button reported"
                              title="You have already reported this job"
                              disabled
                            >
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                <polyline points="22 4 12 14.01 9 11.01"></polyline>
                              </svg>
                              Reported
                            </button>
                          ) : (
                            // If job is neither hidden nor reported, show "Report" button
                            <button 
                              onClick={() => setShowReportModal(true)}
                              className="report-button"
                              title="Report this job"
                            >
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"></path>
                                <line x1="4" y1="22" x2="4" y2="15"></line>
                              </svg>
                              Report
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>

            <div className="job-detail-body">
              <div className="job-detail-section">
                <h3>Job Description</h3>
                <div className="job-detail-text">
                  <JobDescriptionContent content={job.description} />
                </div>
              </div>

              {job.requirements && (
                <div className="job-detail-section">
                  <h3>Requirements</h3>
                  <div className="job-detail-text">
                    <JobDescriptionContent content={job.requirements} emptyMessage="No requirements provided" />
                  </div>
                </div>
              )}

              <div className="job-detail-section">
                <h3>Job Information</h3>
                <div className="job-info-grid">
                  <div className="job-info-item">
                    <span className="job-info-label">Salary</span>
                    <span className="job-info-value">{formatSalary()}</span>
                  </div>
                  {job.experience_level && (
                    <div className="job-info-item">
                      <span className="job-info-label">Experience Level</span>
                      <span className="job-info-value">{job.experience_level}</span>
                    </div>
                  )}
                  {job.location && (
                    <div className="job-info-item">
                      <span className="job-info-label">Location</span>
                      <span className="job-info-value">{job.location}</span>
                    </div>
                  )}
                  {job.timezone && (
                    <div className="job-info-item">
                      <span className="job-info-label">Timezone</span>
                      <span className="job-info-value">{job.timezone}</span>
                    </div>
                  )}
                  {job.application_deadline && (
                    <div className="job-info-item">
                      <span className="job-info-label">Application Deadline</span>
                      <span className="job-info-value">{formatDate(job.application_deadline)}</span>
                    </div>
                  )}
                  <div className="job-info-item">
                    <span className="job-info-label">Posted</span>
                    <span className="job-info-value">{formatDate(job.created_at)}</span>
                  </div>
                </div>
              </div>

              {job.skills_required && job.skills_required.length > 0 && (
                <div className="job-detail-section">
                  <h3>Required Skills</h3>
                  <div className="job-skills-list">
                    {job.skills_required.map((skill, index) => (
                      <span key={index} className="job-skill-tag">{skill}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Share Modal */}
              {job && (
                <JobShareModal
                  isOpen={showShareModal}
                  onClose={() => setShowShareModal(false)}
                  jobId={job.id}
                />
              )}

              {/* Report Modal */}
              {job && user && !(job.posted_by_user_id === (user.acting_user_id ?? user.id) || job.posted_by?.id === (user.acting_user_id ?? user.id) || job.posted_by_user_id?.toString() === String(user.acting_user_id ?? user.id)) && !isReported && (
                <ReportJobModal
                  isOpen={showReportModal}
                  onClose={() => setShowReportModal(false)}
                  jobId={job.id}
                  jobTitle={job.title}
                  onSuccess={() => {
                    setIsReported(true);
                    setReportStatus('pending');
                    fetchJobDetails();
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />
      <BottomNavigationBar 
        onHomeClick={onHomeClick}
        onCategoriesClick={onCategoriesClick}
        onHowItWorksClick={onHowItWorksClick}
        onProfileClick={onProfileClick}
        onAccountSettingsClick={onAccountSettingsClick}
        onForumClick={onForumClick}
        onJobsClick={() => navigate('/vacancies')}
        isLoggedIn={isAuthenticated}
        user={user}
      />

      {showApplicationForm && (
        <JobApplicationForm
          job={job}
          isOpen={showApplicationForm}
          onClose={() => setShowApplicationForm(false)}
          onSuccess={handleApplicationSuccess}
        />
      )}
      </div>
    </>
  );
};

export default JobDetailPage;

